import { useEffect, useCallback, useState, FC } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSBanner,
  MDSBannerKind,
  MDSBannerSlotName,
  MDSButton,
  MDSButtonVariant,
  MDSConfigurationListRow,
  MDSConfigurationListRowSlotName,
  MDSIcon,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { useAppStore } from "@/store";
import {
  CalendarConnectionConfigurationList,
  type CalendarConnectionStatus,
} from "@/components/calendar/calendar-connection-configuration-list";
import type { MDSMenuContentList } from "@/mds";

interface CalendarConnectionsSectionProps {
  onConnectClick?: () => void;
  isOAuthPending?: boolean;
}

export const CalendarConnectionsSection: FC<CalendarConnectionsSectionProps> = observer(
  ({ onConnectClick, isOAuthPending }) => {
    const { store } = useAppStore();
    const googleCalendarStore = store.calendar.google;
    const outlookCalendarStore = store.calendar.outlook;

    useEffect(() => {
      void googleCalendarStore.loadSettings();
    }, [googleCalendarStore]);

    useEffect(() => {
      void outlookCalendarStore.loadSettings();
    }, [outlookCalendarStore]);

    const [togglingCalendar, setTogglingCalendar] = useState<{
      id: string;
      targetEnabled: boolean;
    } | null>(null);

    const isUpdating =
      googleCalendarStore.isUpdatingCalendars || outlookCalendarStore.isUpdatingCalendars;

    useEffect(() => {
      if (!isUpdating) {
        setTogglingCalendar(null);
      }
    }, [isUpdating]);

    const handleToggleCalendar = useCallback(
      (
        calendarId: string,
        isEnabled: boolean,
        subjectId: string,
        provider: "google" | "outlook"
      ) => {
        setTogglingCalendar({ id: calendarId, targetEnabled: isEnabled });
        if (provider === "google") {
          void googleCalendarStore.toggleCalendar({ subjectId, calendarId, isEnabled });
        } else if (provider === "outlook") {
          void outlookCalendarStore.toggleCalendar({ subjectId, calendarId, isEnabled });
        }
      },
      [googleCalendarStore, outlookCalendarStore]
    );

    const handleRetryCalendar = useCallback(
      async (connectedCalendarId: string) => {
        await store.calendar.refreshCalendar(connectedCalendarId);
      },
      [store.calendar]
    );

    const handleRetryGoogleSettings = useCallback(() => {
      void googleCalendarStore.loadSettings();
    }, [googleCalendarStore]);

    const handleRetryOutlookSettings = useCallback(() => {
      void outlookCalendarStore.loadSettings();
    }, [outlookCalendarStore]);

    const createGoogleAccountMenu = useCallback(
      (subjectId: string | null | undefined): MDSMenuContentList => {
        if (!subjectId) return [];
        return [
          {
            id: "remove-account",
            kind: MDSMenuContentItemKind.Item,
            label: "Remove account",
            icon: MDSIconKind.Ban,
            onClick: () => void googleCalendarStore.disconnect(subjectId),
            isDisabled: googleCalendarStore.isDisconnecting,
          },
        ];
      },
      [googleCalendarStore]
    );

    const createOutlookAccountMenu = useCallback(
      (subjectId: string | null | undefined): MDSMenuContentList => {
        if (!subjectId) return [];
        return [
          {
            id: "remove-account",
            kind: MDSMenuContentItemKind.Item,
            label: "Remove account",
            icon: MDSIconKind.Ban,
            onClick: () => void outlookCalendarStore.disconnect(subjectId),
            isDisabled: outlookCalendarStore.isDisconnecting,
          },
        ];
      },
      [outlookCalendarStore]
    );

    const handleGoogleConnect = useCallback(() => {
      onConnectClick?.();
      void googleCalendarStore.connect();
    }, [googleCalendarStore, onConnectClick]);

    const handleOutlookConnect = useCallback(() => {
      onConnectClick?.();
      void outlookCalendarStore.connect();
    }, [outlookCalendarStore, onConnectClick]);

    return (
      <>
        {googleCalendarStore.settingsLoadError && (
          <MDSBanner
            kind={MDSBannerKind.Error}
            iconKind={MDSIconKind.Warning}
            message="We couldn't load your Google Calendar settings. Please try again."
          >
            <MDSButton
              slot={MDSBannerSlotName.Right}
              variant={MDSButtonVariant.Danger}
              onClick={handleRetryGoogleSettings}
              isLoading={googleCalendarStore.isLoading}
            >
              Retry
            </MDSButton>
          </MDSBanner>
        )}

        {outlookCalendarStore.settingsLoadError && (
          <MDSBanner
            kind={MDSBannerKind.Error}
            iconKind={MDSIconKind.Warning}
            message="We couldn't load your Outlook Calendar settings. Please try again."
          >
            <MDSButton
              slot={MDSBannerSlotName.Right}
              variant={MDSButtonVariant.Danger}
              onClick={handleRetryOutlookSettings}
              isLoading={outlookCalendarStore.isLoading}
            >
              Retry
            </MDSButton>
          </MDSBanner>
        )}

        <SectionGroup>
          <SectionHeader>
            <MDSText variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
              CONNECTED CALENDARS
            </MDSText>
          </SectionHeader>

          {store.spaceAccountCalendarConnections.all.map(connection => {
            const email = connection.associatedEmailAddress;
            const isGoogle = connection.providerKind === "GOOGLE_CALENDAR";
            const isOutlook = connection.providerKind === "OUTLOOK_CALENDAR";

            if (!isGoogle && !isOutlook) return null;

            const googleAccount =
              isGoogle && email
                ? googleCalendarStore.accountsList.find(acc => acc.googleAccountEmail === email)
                : undefined;
            const outlookAccount =
              isOutlook && email
                ? outlookCalendarStore.accountsList.find(acc => acc.outlookAccountEmail === email)
                : undefined;

            if (isGoogle && !googleAccount) return null;
            if (isOutlook && !outlookAccount) return null;

            const calendarStore = isGoogle ? googleCalendarStore : outlookCalendarStore;
            const subjectId = isGoogle
              ? (googleAccount?.googleSubjectId ?? "")
              : (outlookAccount?.outlookSubjectId ?? "");
            const provider = isGoogle ? "google" : "outlook";
            const accountMenu = isGoogle
              ? createGoogleAccountMenu(googleAccount?.googleSubjectId)
              : createOutlookAccountMenu(outlookAccount?.outlookSubjectId);

            const settingsCalendars = (
              isGoogle ? (googleAccount?.calendars ?? []) : (outlookAccount?.calendars ?? [])
            ).map(c => ({
              id: c.id,
              summary: c.summary,
              isEnabled: c.isEnabled,
              isPrimary: c.isPrimary,
              backgroundColor: c.backgroundColor,
            }));

            const getConnectionStatus = (): CalendarConnectionStatus => {
              if (calendarStore.isDisconnecting) return "DISCONNECTING";
              if (calendarStore.isConnecting) return "RECONNECTING";
              if (connection.isConnected === false) return "DISCONNECTED";
              if (calendarStore.isLoading) return "LOADING";
              return "CONNECTED";
            };

            return (
              <CalendarConnectionConfigurationList
                key={connection.id}
                connection={connection}
                subjectId={subjectId}
                settingsCalendars={settingsCalendars}
                isTogglingCalendar={isUpdating}
                connectionStatus={getConnectionStatus()}
                onToggleCalendar={(calendarId, isEnabled) =>
                  handleToggleCalendar(calendarId, isEnabled, subjectId, provider)
                }
                onRetryCalendar={handleRetryCalendar}
                refreshingCalendarIds={store.calendar.refreshingCalendarIds}
                accountMenu={accountMenu}
                togglingCalendar={togglingCalendar}
                onConnectionReconnect={() => void calendarStore.connect()}
              />
            );
          })}

          <MDSConfigurationListRow
            title="Connect a Google Calendar"
            description={isOAuthPending ? "Complete sign-in in your browser" : undefined}
          >
            <MDSIcon
              slot={MDSConfigurationListRowSlotName.LeftAddon}
              kind={MDSIconKind.GoogleCalendar}
            />
            <MDSButton
              slot={MDSConfigurationListRowSlotName.RightAddon}
              onClick={handleGoogleConnect}
              isDisabled={googleCalendarStore.isConnecting || isOAuthPending}
              isLoading={isOAuthPending}
            >
              Connect
            </MDSButton>
          </MDSConfigurationListRow>

          <MDSConfigurationListRow
            title="Connect an Outlook Calendar"
            description={isOAuthPending ? "Complete sign-in in your browser" : undefined}
          >
            <MDSIcon
              slot={MDSConfigurationListRowSlotName.LeftAddon}
              kind={MDSIconKind.OutlookCalendar}
            />
            <MDSButton
              slot={MDSConfigurationListRowSlotName.RightAddon}
              onClick={handleOutlookConnect}
              isDisabled={outlookCalendarStore.isConnecting || isOAuthPending}
              isLoading={isOAuthPending}
            >
              Connect
            </MDSButton>
          </MDSConfigurationListRow>
        </SectionGroup>
      </>
    );
  }
);

const SectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
`;

const SectionHeader = styled.div`
  padding-top: ${({ theme }) => theme.spacing[4]};
  padding-bottom: ${({ theme }) => theme.spacing[2]};
`;
